import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  MAX_TELEFONOS_CONTACTO,
  MENSAJE_CLAVE_UNIDAD,
  TIPOS_RESIDENTE,
  TIPOS_TAG,
  TIPOS_UNIDAD,
  esAdministrativo,
  telefonoParaMostrar,
  type Pagina,
} from '@kodica/shared';

import { Iconos, type NombreIcono } from '../componentes/iconos';
import { MapaUbicacion } from '../componentes/MapaUbicacion';
import {
  Boton,
  Buscador,
  Campo,
  Cargando,
  Celda,
  Chip,
  Encabezado,
  Entrada,
  ErrorCaja,
  Fila,
  Modal,
  Paginador,
  Panel,
  Seleccion,
  Tabla,
  Vacio,
} from '../componentes/ui';
import { ErrorApi, pedir, subirArchivo } from '../lib/api';
import { copiarTexto } from '../lib/portapapeles';
import { consulta, useRecurso, useRetardo } from '../lib/consultas';
import { prepararRostro } from '../lib/rostro';
import { useSesion } from '../lib/sesion';

export type Pestana = 'unidades' | 'residentes' | 'vehiculos' | 'tags' | 'tarjetones';

const PESTANAS: { id: Pestana; texto: string; icono: NombreIcono; soloAdmin?: boolean }[] = [
  { id: 'unidades', texto: 'Unidades', icono: 'unidades' },
  { id: 'residentes', texto: 'Residentes', icono: 'personas' },
  { id: 'vehiculos', texto: 'Vehículos', icono: 'vehiculo' },
  { id: 'tags', texto: 'Credenciales', icono: 'credencial', soloAdmin: true },
  { id: 'tarjetones', texto: 'Tarjetones', icono: 'credencial' },
];

const BUSCADOR: Record<Pestana, string> = {
  unidades: 'Buscar por clave: A14, B27…',
  residentes: 'Buscar por nombre o unidad',
  vehiculos: 'Buscar por placa',
  tags: 'Buscar por código',
  tarjetones: 'Buscar por etiqueta: 12, A3…',
};

interface Unidad {
  id: string;
  clave: string;
  tipo: string;
  activa: boolean;
  calle: string | null;
  numero: string | null;
  zona: { nombre: string } | null;
  _count: { residentes: number; vehiculos: number; tags: number; telefonos: number };
}

interface Residente {
  id: string;
  tipo: string;
  unidad: { id: string; clave: string };
  usuario: {
    nombre: string;
    apellidoPaterno: string | null;
    apellidoMaterno: string | null;
    email?: string | null;
    telefono?: string | null;
    activo: boolean;
    /** Si puede entrar a la app. Se deriva en el servidor; el hash nunca sale. */
    tieneAcceso?: boolean;
  };
}

interface Vehiculo {
  id: string;
  placa: string;
  marca: string | null;
  color: string | null;
  activo: boolean;
  unidad: { clave: string };
  tags: { codigo: string; estado: string }[];
}

interface Tarjeton {
  id: string;
  codigo: string;
  etiqueta: string | null;
  estado: 'DISPONIBLE' | 'ENTREGADO' | 'PERDIDO' | 'BAJA';
  motivoEstado: string | null;
  /** Quién lo trae ahora. Vacío salvo que esté entregado. */
  accesos: {
    id: string;
    nombreVisitante: string | null;
    entradaEn: string;
    unidad: { clave: string } | null;
    puntoAcceso: { nombre: string } | null;
  }[];
}

interface Tag {
  id: string;
  codigo: string;
  tipo: string;
  estado: string;
  motivoEstado: string | null;
  unidad: { clave: string };
  vehiculo: { placa: string } | null;
}

const TONO_TAG: Record<string, 'ok' | 'aviso' | 'malo' | 'neutro'> = {
  ACTIVO: 'ok',
  SUSPENDIDO: 'aviso',
  EXTRAVIADO: 'malo',
  BAJA: 'neutro',
};

export function PadronPagina() {
  const { usuario, rolServidor } = useSesion();
  const admin = usuario ? esAdministrativo(usuario.rol) : false;

  /**
   * **Editar el padrón es cosa de la nube.**
   *
   * No es un permiso: en la caseta esas rutas del API ya no existen. El padrón
   * baja de la nube y la bajada reemplaza la fila local, así que editar abajo
   * era una trampa —se guardaba, se veía aplicado, y minutos después volvía
   * solo sin decir nada—. Aquí se esconden los controles para que nadie pulse
   * algo que va a contestar «Cannot POST», y se explica en pantalla a dónde ir.
   *
   * Leer sí se puede, y hace falta: el guardia busca viviendas y residentes
   * todo el turno.
   */
  const puedeEditar = admin && rolServidor === 'nube';

  const [pestana, setPestana] = useState<Pestana>('unidades');
  const [buscar, setBuscar] = useState('');
  const [pagina, setPagina] = useState(1);
  const [alta, setAlta] = useState(false);
  const [editando, setEditando] = useState<Record<string, unknown> | null>(null);

  const busqueda = useRetardo(buscar);
  const ruta = consulta(`/padron/${pestana}`, { pagina, porPagina: 25, buscar: busqueda });
  const { datos, cargando, error, recargar } = useRecurso<Pagina<unknown>>(ruta);

  const visibles = PESTANAS.filter((p) => admin || !p.soloAdmin);

  function editar(registro: Record<string, unknown>) {
    setEditando(registro);
    setAlta(true);
  }

  /**
   * Dar de baja. Se pregunta antes porque no hay deshacer en la interfaz —el
   * dato sigue en la base, pero recuperarlo no es un botón—.
   */
  async function darDeBaja(registro: Record<string, unknown>) {
    const id = registro['id'] as string;

    // Una credencial no se da de baja con DELETE: cambia de estado, y el API
    // **exige el motivo**. Suspender un tag sin decir por qué convierte la
    // bitácora en un misterio el día que el residente reclame por qué no le
    // abre la pluma. Aquí se mandaba un DELETE que no existe y respondía 404:
    // el botón no daba de baja nada, solo un aviso de error.
    if (pestana === 'tags') {
      const motivo = window.prompt(
        '¿Por qué se da de baja esta credencial? Es lo que se le explica al residente después.',
      );
      if (motivo === null) return;
      if (!motivo.trim()) {
        window.alert('Hace falta el motivo.');
        return;
      }
      try {
        await pedir(`/padron/tags/${id}/estado`, {
          metodo: 'PATCH',
          cuerpo: { estado: 'BAJA', motivoEstado: motivo.trim() },
        });
        recargar();
      } catch (e) {
        window.alert(e instanceof ErrorApi ? e.message : 'No se pudo dar de baja');
      }
      return;
    }

    if (!window.confirm('¿Dar de baja este registro? Deja de estar activo, pero no se borra.'))
      return;
    try {
      await pedir(`/padron/${pestana}/${id}`, { metodo: 'DELETE' });
      recargar();
    } catch (e) {
      window.alert(e instanceof ErrorApi ? e.message : 'No se pudo dar de baja');
    }
  }

  const acciones = puedeEditar
    ? {
        alEditar: editar,
        alDarDeBaja: (r: Record<string, unknown>) => void darDeBaja(r),
      }
    : undefined;

  return (
    <>
      <Encabezado titulo="Padrón" descripcion="Unidades, residentes, vehículos y credenciales">
        {puedeEditar && (
          <Boton variante="primario" icono={Iconos.agregar} onClick={() => setAlta(true)}>
            Agregar
          </Boton>
        )}
      </Encabezado>

      {admin && !puedeEditar && (
        <p className="mb-stack-md rounded-md border border-borde bg-superficie px-4 py-3 text-sm text-tenue">
          Esta es la caseta y aquí el padrón <strong>solo se consulta</strong>. Los cambios se hacen
          en la nube, que es de donde baja: hechos aquí se revertirían solos en la siguiente
          sincronización.
        </p>
      )}

      <div className="mb-stack-md flex flex-col gap-stack-md lg:flex-row lg:items-center lg:justify-between">
        <div role="tablist" aria-label="Secciones del padrón" className="flex flex-wrap gap-2">
          {visibles.map((p) => {
            const Icono = Iconos[p.icono];
            const activa = pestana === p.id;
            return (
              <button
                key={p.id}
                role="tab"
                aria-selected={activa}
                onClick={() => {
                  setPestana(p.id);
                  setPagina(1);
                  setBuscar('');
                }}
                className={`toque inline-flex items-center gap-2 rounded-lg px-4 py-2 text-label-bold transition-all ${
                  activa
                    ? 'bg-secondary-container text-on-secondary-container'
                    : 'border border-outline-variant bg-surface-container-lowest text-on-surface-variant hover:bg-surface-container-high'
                }`}
              >
                <Icono tam={20} />
                {p.texto}
              </button>
            );
          })}
        </div>

        <div className="w-full lg:max-w-sm">
          <Buscador
            value={buscar}
            onChange={(e) => {
              setBuscar(e.target.value);
              setPagina(1);
            }}
            placeholder={BUSCADOR[pestana]}
          />
        </div>
      </div>

      <Panel>
        {error && <ErrorCaja>{error}</ErrorCaja>}
        {cargando && <Cargando />}
        {!cargando && !error && datos && datos.total === 0 && (
          <Vacio>
            {busqueda ? `Nada coincide con "${busqueda}".` : 'Todavía no hay registros aquí.'}
          </Vacio>
        )}

        {!cargando && !error && datos && datos.total > 0 && (
          <>
            {pestana === 'unidades' && (
              <TablaUnidades filas={datos.datos as Unidad[]} acciones={acciones} />
            )}
            {pestana === 'residentes' && (
              <>
                <SinTelefono admin={admin} />
                <TablaResidentes
                  filas={datos.datos as Residente[]}
                  admin={admin}
                  puedeEditar={puedeEditar}
                  acciones={acciones}
                />
              </>
            )}
            {pestana === 'vehiculos' && (
              <TablaVehiculos filas={datos.datos as Vehiculo[]} acciones={acciones} />
            )}
            {pestana === 'tags' && <TablaTags filas={datos.datos as Tag[]} acciones={acciones} />}
            {pestana === 'tarjetones' && (
              <TablaTarjetones filas={datos.datos as Tarjeton[]} alCambiar={recargar} />
            )}
            <Paginador
              pagina={datos.pagina}
              paginas={datos.paginas}
              total={datos.total}
              alCambiar={setPagina}
            />
          </>
        )}
      </Panel>

      <ModalAlta
        pestana={pestana}
        abierto={alta}
        registro={editando}
        alCerrar={() => {
          setAlta(false);
          setEditando(null);
        }}
        alGuardar={() => {
          setAlta(false);
          setEditando(null);
          recargar();
        }}
      />
    </>
  );
}

/**
 * Convierte un registro de la lista en los campos del formulario.
 *
 * Los listados vienen anidados —el residente trae su `usuario` dentro, todos
 * traen su `unidad`— y el formulario es plano. Se aplana un solo nivel y se
 * quedan las cadenas y los números: lo demás (fechas, contadores, relaciones)
 * no se edita aquí.
 */
/** Lo que cada tabla necesita para ofrecer Editar y Dar de baja. */
interface Acciones {
  alEditar: (registro: Record<string, unknown>) => void;
  alDarDeBaja: (registro: Record<string, unknown>) => void;
}

function aplanarParaFormulario(registro: Record<string, unknown>): Record<string, string> {
  const campos: Record<string, string> = {};
  const tomar = (fuente: Record<string, unknown>) => {
    for (const [clave, valor] of Object.entries(fuente)) {
      if (clave === 'id' || clave === 'activo' || clave === 'activa') continue;
      // Las listas de cadenas —los teléfonos de contacto del titular— se guardan
      // en el formulario separadas por comas, porque el estado es un mapa plano.
      // Sin esto llegaban como arreglo, se descartaban aquí, y quien editaba a
      // un titular veía sus teléfonos en blanco: parecía que no se habían
      // guardado y en realidad esta función no los traía.
      if (Array.isArray(valor) && valor.every((x) => typeof x === 'string')) {
        campos[clave] = valor.join(',');
        continue;
      }
      if (typeof valor === 'string' || typeof valor === 'number') campos[clave] = String(valor);
    }
  };
  tomar(registro);
  const usuario = registro['usuario'];
  if (usuario && typeof usuario === 'object') tomar(usuario as Record<string, unknown>);
  const unidad = registro['unidad'];
  if (unidad && typeof unidad === 'object') {
    const u = unidad as Record<string, unknown>;
    if (typeof u['id'] === 'string') campos['unidadId'] = u['id'];
  }
  return campos;
}

/**
 * Editar y dar de baja, al final de cada renglón.
 *
 * «Dar de baja» y no «Eliminar»: en Kodica nada se borra. La unidad de alguien
 * que se mudó sigue colgando de sus accesos históricos, y esa es justamente la
 * bitácora que sostiene una aclaración. El botón dice lo que hace.
 */
function CeldaAcciones({
  alEditar,
  alDarDeBaja,
  activo,
}: {
  alEditar: () => void;
  alDarDeBaja: () => void;
  activo: boolean;
}) {
  return (
    <Celda>
      <div className="flex justify-end gap-1">
        <Boton variante="texto" onClick={alEditar}>
          Editar
        </Boton>
        {activo && (
          <Boton variante="texto" onClick={alDarDeBaja}>
            Dar de baja
          </Boton>
        )}
      </div>
    </Celda>
  );
}

/**
 * Los teléfonos de contacto del titular: a dónde se le **avisa**.
 *
 * No confundir con los de la vivienda, que son a los que la caseta **llama** y
 * se capturan en la ficha de la unidad. Son dos cosas con dos destinos: el
 * aviso llega al celular de la persona; la llamada de la reja va al número de
 * la casa, que puede contestar cualquiera que esté ahí.
 *
 * Se guardan separados por comas en el estado del formulario, que es un mapa
 * plano de cadenas, y se parten al enviar.
 */
function TelefonosDeContacto({
  valor,
  alCambiar,
}: {
  valor: string;
  alCambiar: (v: string) => void;
}) {
  const lista = valor ? valor.split(',') : [''];
  const fijar = (nueva: string[]) => alCambiar(nueva.join(','));

  return (
    <Campo
      etiqueta="Teléfonos de contacto"
      ayuda={`A dónde se le avisa de las visitas. Hasta ${MAX_TELEFONOS_CONTACTO}.`}
    >
      <div className="mt-1 space-y-2">
        {lista.map((t, i) => (
          <div key={i} className="flex items-center gap-2">
            <Entrada
              type="tel"
              value={t}
              placeholder={i === 0 ? 'Celular' : 'Otro número'}
              onChange={(e) => fijar(lista.map((x, k) => (k === i ? e.target.value : x)))}
            />
            {lista.length > 1 && (
              <Boton
                variante="texto"
                aria-label="Quitar"
                onClick={() => fijar(lista.filter((_, k) => k !== i))}
              >
                Quitar
              </Boton>
            )}
          </div>
        ))}
        {lista.length < MAX_TELEFONOS_CONTACTO && (
          <Boton variante="texto" icono={Iconos.agregar} onClick={() => fijar([...lista, ''])}>
            Agregar otro
          </Boton>
        )}
      </div>
    </Campo>
  );
}

// ── Tablas ────────────────────────────────────────────────────

function TablaUnidades({ filas, acciones }: { filas: Unidad[]; acciones?: Acciones }) {
  return (
    <Tabla
      cabeceras={[
        'Clave',
        'Tipo',
        'Zona',
        'Residentes',
        'Vehículos',
        'Teléfonos de la caseta',
        'Estado',
        ...(acciones ? [''] : []),
      ]}
    >
      {filas.map((u) => (
        <Fila key={u.id}>
          <Celda fuerte>
            {/* La clave es el enlace a la ficha: es lo que el administrador
                busca cuando alguien reclama algo de esa vivienda. */}
            <Link to={`/padron/unidad/${u.id}`} className="text-primary hover:underline">
              {u.clave}
            </Link>
          </Celda>
          <Celda>{u.tipo.toLowerCase()}</Celda>
          <Celda>{u.zona?.nombre ?? '—'}</Celda>
          <Celda>{u._count.residentes}</Celda>
          <Celda>{u._count.vehiculos}</Celda>
          <Celda>
            {/* Sin números, el botón «Llamar» del guardia no puede hacer nada, y
                él lo descubre con el visitante enfrente. Se ve desde el listado
                para que el hueco salte antes, no en la reja. */}
            {u._count.telefonos > 0 ? (
              <Link to={`/padron/unidad/${u.id}`} className="text-primary hover:underline">
                {u._count.telefonos}
              </Link>
            ) : (
              <Link to={`/padron/unidad/${u.id}`}>
                <Chip tono="aviso">Sin teléfonos</Chip>
              </Link>
            )}
          </Celda>
          <Celda>
            <Chip tono={u.activa ? 'ok' : 'neutro'}>{u.activa ? 'Activa' : 'Baja'}</Chip>
          </Celda>
          {acciones && (
            <CeldaAcciones
              activo={u.activa}
              alEditar={() => acciones.alEditar(u as unknown as Record<string, unknown>)}
              alDarDeBaja={() => acciones.alDarDeBaja(u as unknown as Record<string, unknown>)}
            />
          )}
        </Fila>
      ))}
    </Tabla>
  );
}

function TablaResidentes({
  filas,
  admin,
  puedeEditar,
  acciones,
}: {
  filas: Residente[];
  admin: boolean;
  /** Ver la nota en `PadronPagina`: dar y quitar acceso escribe en el padrón. */
  puedeEditar: boolean;
  acciones?: Acciones;
}) {
  // Sin columna de contacto para el guardia. El API tampoco la manda: la
  // promesa de docs/07 es que el guardia nunca tiene los teléfonos. El rostro
  // también es cosa del administrador (dato biométrico).
  const [rostroDe, setRostroDe] = useState<Residente | null>(null);
  const [acceso, setAcceso] = useState<{
    quien: string;
    identificador: string;
    clave: string;
  } | null>(null);
  const cabeceras = admin
    ? [
        'Nombre',
        'Unidad',
        'Tipo',
        'Contacto',
        ...(puedeEditar ? ['App'] : []),
        'Rostro',
        'Estado',
        ...(acciones ? [''] : []),
      ]
    : ['Nombre', 'Unidad', 'Tipo', 'Estado'];

  return (
    <>
      <Tabla cabeceras={cabeceras}>
        {filas.map((r) => (
          <Fila key={r.id}>
            <Celda fuerte>
              {r.usuario.nombre} {r.usuario.apellidoPaterno ?? ''} {r.usuario.apellidoMaterno ?? ''}
            </Celda>
            <Celda fuerte>{r.unidad.clave}</Celda>
            <Celda>{r.tipo.toLowerCase()}</Celda>
            {admin && (
              <Celda>
                {r.usuario.telefono
                  ? telefonoParaMostrar(r.usuario.telefono)
                  : (r.usuario.email ?? '—')}
              </Celda>
            )}
            {/* Dar y quitar la llave de la app escribe en `Usuario`, que baja de
                la nube: solo desde arriba. La columna de rostro se queda en las
                dos porque el guardia necesita ver si alguien tiene cara
                enrolada; lo que no puede hacer aquí es cambiarla. */}
            {puedeEditar && (
              <Celda>
                <AccesoApp residente={r} alDar={setAcceso} />
              </Celda>
            )}
            {admin && (
              <Celda>
                <Boton variante="texto" icono={Iconos.personas} onClick={() => setRostroDe(r)}>
                  Rostro
                </Boton>
              </Celda>
            )}
            <Celda>
              <Chip tono={r.usuario.activo ? 'ok' : 'neutro'}>
                {r.usuario.activo ? 'Activo' : 'Baja'}
              </Chip>
            </Celda>
            {acciones && (
              <CeldaAcciones
                activo={r.usuario.activo}
                alEditar={() => acciones.alEditar(r as unknown as Record<string, unknown>)}
                alDarDeBaja={() => acciones.alDarDeBaja(r as unknown as Record<string, unknown>)}
              />
            )}
          </Fila>
        ))}
      </Tabla>
      <ModalRostro
        key={rostroDe?.id ?? 'ninguno'}
        residente={rostroDe}
        puedeEditar={puedeEditar}
        alCerrar={() => setRostroDe(null)}
      />
      {acceso && <ModalAcceso datos={acceso} alCerrar={() => setAcceso(null)} />}
    </>
  );
}

interface EstadoRostro {
  rostro: {
    numeroTerminal: number;
    nombre: string;
    activo: boolean;
    enrolamientos: Array<{
      estado: 'PENDIENTE' | 'ENROLADO' | 'ERROR' | 'BORRANDO';
      error: string | null;
      enroladoEn: string | null;
      dispositivo: { id: string; nombre: string };
    }>;
  } | null;
}

const TONO_ENROL: Record<string, 'ok' | 'aviso' | 'malo' | 'neutro'> = {
  ENROLADO: 'ok',
  PENDIENTE: 'aviso',
  ERROR: 'malo',
  BORRANDO: 'neutro',
};

/**
 * Gestión del rostro de un residente: ver cómo quedó en cada terminal, capturar
 * una foto nueva y quitarla. La foto se reescala en el navegador antes de subir,
 * porque el terminal solo traga fotos chicas (ver lib/rostro).
 */
function ModalRostro({
  residente,
  alCerrar,
  puedeEditar,
}: {
  residente: Residente | null;
  alCerrar: () => void;
  puedeEditar: boolean;
}) {
  const estado = useRecurso<EstadoRostro>(
    residente ? `/facial/residentes/${residente.id}/rostro` : null,
  );
  const [archivo, setArchivo] = useState<Blob | null>(null);
  const [previa, setPrevia] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function elegir(e: React.ChangeEvent<HTMLInputElement>) {
    const entrada = e.target.files?.[0];
    e.target.value = ''; // permite volver a elegir el mismo archivo
    if (!entrada) return;
    setError(null);
    try {
      const jpeg = await prepararRostro(entrada);
      setArchivo(jpeg);
      setPrevia((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(jpeg);
      });
    } catch {
      setError('No se pudo leer la imagen. Prueba con otra foto.');
    }
  }

  async function enrolar() {
    if (!archivo || !residente) return;
    setTrabajando(true);
    setError(null);
    try {
      await subirArchivo(`/facial/residentes/${residente.id}/rostro`, archivo, 'rostro.jpg');
      setArchivo(null);
      setPrevia(null);
      estado.recargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo enrolar el rostro');
    } finally {
      setTrabajando(false);
    }
  }

  async function quitar() {
    if (!residente) return;
    if (!window.confirm('¿Quitar el rostro de los terminales y del servidor?')) return;
    setTrabajando(true);
    setError(null);
    try {
      await pedir(`/facial/residentes/${residente.id}/rostro`, { metodo: 'DELETE' });
      estado.recargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo quitar el rostro');
    } finally {
      setTrabajando(false);
    }
  }

  const nombre = residente
    ? `${residente.usuario.nombre} ${residente.usuario.apellidoPaterno ?? ''}`.trim()
    : '';
  const rostro = estado.datos?.rostro ?? null;

  return (
    <Modal titulo={`Rostro · ${nombre}`} abierto={residente !== null} alCerrar={alCerrar}>
      <div className="space-y-5">
        {estado.cargando && <Cargando />}

        {!estado.cargando && (
          <>
            {rostro ? (
              <div className="space-y-2">
                <p className="text-body-md text-on-surface">
                  Enrolado como <strong>{rostro.nombre}</strong>
                </p>
                {rostro.enrolamientos.length === 0 && (
                  <p className="text-label-sm text-on-surface-variant">
                    Guardado en el servidor; aún no hay terminal facial al que enviarlo.
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {rostro.enrolamientos.map((en) => (
                    <Chip key={en.dispositivo.id} tono={TONO_ENROL[en.estado] ?? 'neutro'}>
                      {en.dispositivo.nombre}: {en.estado.toLowerCase()}
                      {en.error ? ` — ${en.error}` : ''}
                    </Chip>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-body-md text-on-surface-variant">
                Esta persona no tiene rostro enrolado.
              </p>
            )}

            {error && <ErrorCaja>{error}</ErrorCaja>}

            {previa && (
              <img
                src={previa}
                alt="Vista previa del rostro"
                className="mx-auto h-48 w-48 rounded-xl object-cover"
              />
            )}

            {/* En la caseta esto solo se mira. Las rutas de alta y baja no
                existen en ese perfil, así que enseñar los controles sería
                mandar a alguien a un «Cannot POST»; y aunque existieran, el
                alta hecha aquí no llegaría a la nube ni a las demás casetas. */}
            {!puedeEditar ? (
              <p className="text-label-sm text-on-surface-variant">
                El rostro se da de alta en la nube, que es de donde baja a los terminales de todas
                las casetas.
              </p>
            ) : (
              <>
                <label className="block">
                  <span className="sr-only">Elegir foto del rostro</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={elegir}
                    className="block w-full text-body-md text-on-surface-variant file:mr-4 file:rounded-lg file:border-0 file:bg-surface-container-high file:px-4 file:py-2 file:text-label-bold file:text-on-surface"
                  />
                </label>
                <p className="text-label-sm text-on-surface-variant">
                  De frente, buena luz, una sola cara. La foto se ajusta sola al tamaño del
                  terminal.
                </p>

                <div className="flex items-center justify-between gap-3 pt-2">
                  {rostro ? (
                    <Boton variante="texto" onClick={quitar} disabled={trabajando}>
                      Quitar rostro
                    </Boton>
                  ) : (
                    <span />
                  )}
                  <Boton
                    variante="primario"
                    icono={Iconos.correcto}
                    onClick={enrolar}
                    disabled={!archivo || trabajando}
                  >
                    {trabajando ? 'Enviando…' : rostro ? 'Reemplazar' : 'Enrolar'}
                  </Boton>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function TablaVehiculos({ filas, acciones }: { filas: Vehiculo[]; acciones?: Acciones }) {
  return (
    <Tabla
      cabeceras={[
        'Placa',
        'Unidad',
        'Marca',
        'Color',
        'Credenciales',
        'Estado',
        ...(acciones ? [''] : []),
      ]}
    >
      {filas.map((v) => (
        <Fila key={v.id}>
          <Celda mono fuerte>
            {v.placa}
          </Celda>
          <Celda fuerte>{v.unidad.clave}</Celda>
          <Celda>{v.marca ?? '—'}</Celda>
          <Celda>{v.color ?? '—'}</Celda>
          <Celda>{v.tags.length}</Celda>
          <Celda>
            <Chip tono={v.activo ? 'ok' : 'neutro'}>{v.activo ? 'Activo' : 'Baja'}</Chip>
          </Celda>
          {acciones && (
            <CeldaAcciones
              activo={v.activo}
              alEditar={() => acciones.alEditar(v as unknown as Record<string, unknown>)}
              alDarDeBaja={() => acciones.alDarDeBaja(v as unknown as Record<string, unknown>)}
            />
          )}
        </Fila>
      ))}
    </Tabla>
  );
}

function TablaTags({ filas, acciones }: { filas: Tag[]; acciones?: Acciones }) {
  return (
    <Tabla
      cabeceras={[
        'Código',
        'Tipo',
        'Unidad',
        'Vehículo',
        'Estado',
        'Motivo',
        ...(acciones ? [''] : []),
      ]}
    >
      {filas.map((t) => (
        <Fila key={t.id}>
          <Celda mono fuerte>
            {t.codigo}
          </Celda>
          <Celda>{t.tipo.replace(/_/g, ' ').toLowerCase()}</Celda>
          <Celda fuerte>{t.unidad.clave}</Celda>
          <Celda mono>{t.vehiculo?.placa ?? '—'}</Celda>
          <Celda>
            <Chip tono={TONO_TAG[t.estado] ?? 'neutro'}>{t.estado.toLowerCase()}</Chip>
          </Celda>
          <Celda>{t.motivoEstado ?? '—'}</Celda>
          {acciones && (
            <CeldaAcciones
              activo={t.estado === 'ACTIVO'}
              alEditar={() => acciones.alEditar(t as unknown as Record<string, unknown>)}
              alDarDeBaja={() => acciones.alDarDeBaja(t as unknown as Record<string, unknown>)}
            />
          )}
        </Fila>
      ))}
    </Tabla>
  );
}

// ── Alta ──────────────────────────────────────────────────────

/** Campos que pintan el error del servidor bajo su etiqueta, no en la caja general. */
const CAMPOS_CON_ERROR_PROPIO = new Set(['clave']);

const TITULOS: Record<Pestana, string> = {
  unidades: 'Nueva unidad',
  residentes: 'Nueva persona',
  vehiculos: 'Nuevo vehículo',
  tags: 'Nueva credencial',
  tarjetones: 'Nuevo tarjetón',
};

const TITULOS_EDICION: Record<Pestana, string> = {
  unidades: 'Editar unidad',
  residentes: 'Editar persona',
  vehiculos: 'Editar vehículo',
  tags: 'Editar credencial',
  tarjetones: 'Editar tarjetón',
};

/**
 * Alta y edición. El mismo formulario para las dos cosas: los campos son
 * idénticos y mantener dos pantallas gemelas garantiza que se separen.
 *
 * Con `registro` presente hace PATCH sobre él; sin él, POST. El API tenía los
 * cuatro CRUD desde el principio y la interfaz solo sabía crear — corregir una
 * placa mal tecleada o suspenderle el tag a un moroso exigía entrar a la base
 * de datos.
 */
export function ModalAlta({
  pestana,
  abierto,
  registro,
  unidadFija,
  camposIniciales,
  vehiculos,
  alPedirVehiculo,
  alCerrar,
  alGuardar,
}: {
  pestana: Pestana;
  abierto: boolean;
  /** El que se edita, o `null` para dar de alta uno nuevo. */
  registro: Record<string, unknown> | null;
  /**
   * La vivienda a la que pertenece esto, cuando se llega desde su ficha.
   *
   * Va **fijada y sin forma de cambiarla**: quien abrió la ficha de la A-14 ya
   * dijo de qué casa habla. Dejar el selector ahí reabre la puerta a dar de
   * alta al residente de otra vivienda, que es justo lo que este camino evita.
   */
  unidadFija?: { id: string; clave: string; calle: string | null; numero: string | null };
  /** Con qué arrancar el formulario. Sirve para volver de un desvío sin que se
   *  pierda lo ya tecleado. */
  camposIniciales?: Record<string, string>;
  /** Los de la vivienda, para colgarles la credencial. */
  vehiculos?: { id: string; placa: string }[];
  /** El desvío a dar de alta un vehículo desde la credencial, con lo tecleado. */
  alPedirVehiculo?: (campos: Record<string, string>) => void;
  alCerrar: () => void;
  alGuardar: (creado?: Record<string, unknown>) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [erroresCampo, setErroresCampo] = useState<Record<string, string>>({});
  const [campos, setCampos] = useState<Record<string, string>>({});
  const [enviando, setEnviando] = useState(false);

  // Se rellena al abrir y se vacía al cerrar. La identidad del registro es la
  // dependencia: abrir otro renglón tiene que traer sus datos, no los del
  // anterior.
  const idRegistro = registro?.['id'] as string | undefined;
  useEffect(() => {
    setCampos(
      registro
        ? aplanarParaFormulario(registro)
        : { ...(unidadFija ? { unidadId: unidadFija.id } : {}), ...camposIniciales },
    );
    setError(null);
    setErroresCampo({});
  }, [idRegistro, abierto, registro, unidadFija, camposIniciales]);

  /**
   * Las unidades del selector se **buscan**, no se traen todas.
   *
   * Antes se pedían las primeras 200 y punto: en un fraccionamiento con más,
   * las que faltaban simplemente no aparecían en la lista y nadie se enteraba
   * de por qué no podía dar de alta a alguien de la B-14. Ahora se escribe la
   * clave y el servidor filtra.
   */
  const [buscaUnidad, setBuscaUnidad] = useState('');
  const unidadBuscada = useRetardo(buscaUnidad);
  const unidades = useRecurso<Pagina<Unidad>>(
    // Con la vivienda fijada no hay nada que buscar: la trae quien abrió la ficha.
    abierto && pestana !== 'unidades' && !unidadFija
      ? consulta('/padron/unidades', { porPagina: 20, buscar: unidadBuscada })
      : null,
  );

  function set(k: string, v: string) {
    setCampos((c) => ({ ...c, [k]: v }));
  }

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setErroresCampo({});
    setEnviando(true);
    try {
      // Lo guardado se devuelve: quien vino desviado a crear un vehículo desde
      // una credencial necesita su id para volver con él ya elegido.
      const guardado = await pedir<Record<string, unknown>>(
        idRegistro ? `/padron/${pestana}/${idRegistro}` : `/padron/${pestana}`,
        { metodo: idRegistro ? 'PATCH' : 'POST', cuerpo: limpiar(campos) },
      );
      setCampos({});
      alGuardar(guardado);
    } catch (err) {
      if (!(err instanceof ErrorApi)) {
        setError('No se pudo guardar');
        return;
      }
      const detalles = err.errores ?? [];
      setErroresCampo(Object.fromEntries(detalles.map((x) => [x.campo, x.mensaje])));
      // Lo que ya se ve pegado a su campo no se repite arriba; lo demás sí, o
      // nadie sabría qué corregir.
      const sueltos = detalles
        .filter((x) => !CAMPOS_CON_ERROR_PROPIO.has(x.campo))
        .map((x) => `${x.campo}: ${x.mensaje}`);
      setError([err.message, ...sueltos].join(' · '));
    } finally {
      setEnviando(false);
    }
  }

  const listaUnidades = unidades.datos?.datos ?? [];
  const elegida = listaUnidades.find((u) => u.id === campos['unidadId']);

  /**
   * La vivienda se **busca**, no se elige de una lista.
   *
   * Un fraccionamiento tiene cientos: enseñarlas todas obliga a recorrer una
   * lista enorme, y de memoria "A14" y "B14" no se distinguen. Por eso cada
   * renglón muestra también la calle — quien da de alta a alguien suele tener
   * delante un comprobante de domicilio, no el código interno.
   */
  // Fijada: se enseña para que quien captura sepa en qué casa está parado, pero
  // sin «Cambiar». No es una elección, es el contexto del que vino.
  const unidadFijada = unidadFija && (
    <Campo etiqueta="Unidad">
      <div className="mt-1 rounded-lg border border-outline-variant bg-surface-container px-4 py-3">
        <p className="text-label-bold text-on-surface">{unidadFija.clave}</p>
        <p className="truncate text-label-sm text-on-surface-variant">
          {[unidadFija.calle, unidadFija.numero].filter(Boolean).join(' ') ||
            'Sin domicilio capturado'}
        </p>
      </div>
    </Campo>
  );

  const selectorUnidad = unidadFijada || (
    <Campo etiqueta="Unidad" ayuda={elegida ? undefined : 'Busca por clave o por calle'}>
      {elegida ? (
        <div className="mt-1 flex items-center justify-between gap-3 rounded-lg border border-primary bg-primary-container/20 px-4 py-3">
          <div className="min-w-0">
            <p className="text-label-bold text-on-surface">{elegida.clave}</p>
            {(elegida.calle || elegida.numero) && (
              <p className="truncate text-label-sm text-on-surface-variant">
                {[elegida.calle, elegida.numero].filter(Boolean).join(' ')}
              </p>
            )}
          </div>
          <Boton
            variante="texto"
            onClick={() => {
              set('unidadId', '');
              setBuscaUnidad('');
            }}
          >
            Cambiar
          </Boton>
        </div>
      ) : (
        <>
          <Entrada
            value={buscaUnidad}
            onChange={(e) => setBuscaUnidad(e.target.value)}
            placeholder="A14, o el nombre de la calle"
            autoComplete="off"
          />
          {/* Se despliega solo al escribir: sin esto la lista aparece con las
              primeras veinte nada más abrir el formulario y tapa lo demás. */}
          {buscaUnidad && (
            <ul className="mt-1 max-h-56 overflow-y-auto rounded-lg border border-outline-variant">
              {listaUnidades.map((u) => (
                <li key={u.id} className="border-b border-outline-variant last:border-0">
                  <button
                    type="button"
                    onClick={() => set('unidadId', u.id)}
                    className="toque flex w-full flex-col items-start px-4 py-2 text-left transition-colors hover:bg-surface-container-high"
                  >
                    <span className="text-label-bold text-on-surface">{u.clave}</span>
                    <span className="text-label-sm text-on-surface-variant">
                      {[u.calle, u.numero].filter(Boolean).join(' ') || 'Sin domicilio capturado'}
                    </span>
                  </button>
                </li>
              ))}
              {listaUnidades.length === 0 && !unidades.cargando && (
                <li className="px-4 py-3 text-body-md text-on-surface-variant">
                  Ninguna vivienda coincide con «{buscaUnidad}».
                </li>
              )}
            </ul>
          )}
        </>
      )}
    </Campo>
  );

  return (
    <Modal
      titulo={idRegistro ? TITULOS_EDICION[pestana] : TITULOS[pestana]}
      abierto={abierto}
      alCerrar={alCerrar}
    >
      <form onSubmit={enviar} className="space-y-4">
        {pestana === 'unidades' && (
          <>
            <Campo etiqueta="Clave" ayuda={MENSAJE_CLAVE_UNIDAD} error={erroresCampo['clave']}>
              <Entrada
                required
                autoCapitalize="characters"
                placeholder="A14"
                value={campos['clave'] ?? ''}
                onChange={(e) => set('clave', e.target.value)}
              />
            </Campo>
            <Campo etiqueta="Tipo">
              <Seleccion
                value={campos['tipo'] ?? 'CASA'}
                onChange={(e) => set('tipo', e.target.value)}
              >
                {TIPOS_UNIDAD.map((t) => (
                  <option key={t} value={t}>
                    {t.toLowerCase()}
                  </option>
                ))}
              </Seleccion>
            </Campo>

            {/* Plegado a propósito. En casi todos los fraccionamientos la calle
                y el código postal son los del condominio, y obligar a repetirlos
                en trescientas altas es cómo se consigue que nadie capture
                ninguna. Quien lo necesite lo abre. */}
            {/* Abierto de entrada cuando la vivienda ya trae domicilio: si
                alguien capturó la calle o marcó la casa en el mapa, esconderle
                justo eso al editar es lo mismo que no tenerlo. */}
            <details
              open={Boolean(registro?.['calle'] || registro?.['lat'])}
              className="rounded-lg border border-outline-variant p-4"
            >
              <summary className="cursor-pointer text-label-bold text-on-surface-variant">
                Domicilio y ubicación (opcional)
              </summary>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Campo etiqueta="Calle">
                  <Entrada
                    value={campos['calle'] ?? ''}
                    onChange={(e) => set('calle', e.target.value)}
                  />
                </Campo>
                <Campo etiqueta="Número">
                  <Entrada
                    value={campos['numero'] ?? ''}
                    onChange={(e) => set('numero', e.target.value)}
                  />
                </Campo>
                <Campo etiqueta="Ciudad">
                  <Entrada
                    value={campos['ciudad'] ?? ''}
                    onChange={(e) => set('ciudad', e.target.value)}
                  />
                </Campo>
                <Campo etiqueta="Municipio">
                  <Entrada
                    value={campos['municipio'] ?? ''}
                    onChange={(e) => set('municipio', e.target.value)}
                  />
                </Campo>
                <Campo etiqueta="Código postal">
                  <Entrada
                    value={campos['codigoPostal'] ?? ''}
                    onChange={(e) => set('codigoPostal', e.target.value)}
                  />
                </Campo>
                <Campo
                  etiqueta="Ubicación GPS"
                  ayuda="Tócala en el mapa, o pega las coordenadas: 20.6736, -103.4054"
                >
                  <Entrada
                    value={
                      campos['lat'] && campos['lng'] ? `${campos['lat']}, ${campos['lng']}` : ''
                    }
                    placeholder="20.6736, -103.4054"
                    onChange={(e) => {
                      const [la, ln] = e.target.value.split(',').map((x) => x.trim());
                      set('lat', la ?? '');
                      set('lng', ln ?? '');
                    }}
                  />
                </Campo>
              </div>

              <div className="mt-4">
                <MapaUbicacion
                  lat={Number(campos['lat']) || null}
                  lng={Number(campos['lng']) || null}
                  alElegir={(la, ln) => {
                    set('lat', String(la));
                    set('lng', String(ln));
                  }}
                />
              </div>
            </details>
          </>
        )}

        {pestana === 'residentes' && (
          <>
            <Campo etiqueta="Nombre">
              <Entrada
                required
                value={campos['nombre'] ?? ''}
                onChange={(e) => set('nombre', e.target.value)}
              />
            </Campo>
            <Campo etiqueta="Apellido paterno">
              <Entrada
                required
                value={campos['apellidoPaterno'] ?? ''}
                onChange={(e) => set('apellidoPaterno', e.target.value)}
              />
            </Campo>
            <Campo etiqueta="Apellido materno">
              <Entrada
                value={campos['apellidoMaterno'] ?? ''}
                onChange={(e) => set('apellidoMaterno', e.target.value)}
              />
            </Campo>
            {selectorUnidad}
            <Campo
              etiqueta="Teléfono"
              ayuda="A donde llega el aviso, a donde se marca, y con lo que recupera su contraseña"
            >
              {/* `required` porque el esquema ya lo exige: sin esto el formulario
                  deja mandarlo vacio y el error llega del servidor despues de
                  teclear toda la ficha. */}
              <Entrada
                required
                type="tel"
                value={campos['telefono'] ?? ''}
                onChange={(e) => set('telefono', e.target.value)}
              />
            </Campo>
            <Campo etiqueta="Correo">
              <Entrada
                type="email"
                autoCapitalize="none"
                value={campos['email'] ?? ''}
                onChange={(e) => set('email', e.target.value)}
              />
            </Campo>
            {/* Dos tarjetas y no un desplegable: la diferencia entre titular y
                habitante decide quién puede generar códigos de ingreso y a quién
                llama la caseta. Escondida en un `select` con dos palabras, se
                elige la primera opción sin leerla. */}
            <Campo etiqueta="Qué es en esta vivienda">
              <div className="mt-1 grid gap-2 sm:grid-cols-2">
                {(
                  [
                    {
                      valor: 'TITULAR',
                      titulo: 'Titular',
                      texto:
                        'Responde por la vivienda, genera códigos y recibe los avisos. Máximo dos.',
                    },
                    {
                      valor: 'HABITANTE',
                      titulo: 'Habitante',
                      texto: 'Vive aquí. No genera códigos, salvo que un titular se lo habilite.',
                    },
                  ] as const
                ).map((o) => {
                  const elegido = (campos['tipo'] ?? 'TITULAR') === o.valor;
                  return (
                    <button
                      key={o.valor}
                      type="button"
                      aria-pressed={elegido}
                      onClick={() => set('tipo', o.valor)}
                      className={`toque rounded-lg border-2 p-4 text-left transition-all ${
                        elegido
                          ? 'border-primary bg-primary-container/20'
                          : 'border-outline-variant bg-surface-container-lowest'
                      }`}
                    >
                      <p className="text-label-bold text-on-surface">{o.titulo}</p>
                      <p className="mt-1 text-label-sm text-on-surface-variant">{o.texto}</p>
                    </button>
                  );
                })}
              </div>
            </Campo>

            {/* Solo para el titular, y por eso aparece al elegirlo: es quien
                responde por la vivienda y a quien se le avisa. Un habitante no
                tiene por qué dar su teléfono para vivir donde vive.

                Son **varios** a propósito —hasta tres— porque una persona tiene
                celular y trabajo, y el aviso que no llega al primero puede
                llegar al segundo. Es distinto de los teléfonos de la vivienda,
                que son a los que marca la caseta y viven en su ficha. */}
            {(campos['tipo'] ?? 'TITULAR') === 'TITULAR' && (
              <TelefonosDeContacto
                valor={campos['telefonosContacto'] ?? ''}
                alCambiar={(v) => set('telefonosContacto', v)}
              />
            )}
          </>
        )}

        {pestana === 'vehiculos' && (
          <>
            {selectorUnidad}
            <Campo etiqueta="Placa" ayuda="Se guarda normalizada: sin espacios ni guiones">
              <Entrada
                required
                autoCapitalize="characters"
                value={campos['placa'] ?? ''}
                onChange={(e) => set('placa', e.target.value)}
              />
            </Campo>
            <Campo etiqueta="Marca">
              <Entrada
                value={campos['marca'] ?? ''}
                onChange={(e) => set('marca', e.target.value)}
              />
            </Campo>
            <Campo etiqueta="Color">
              <Entrada
                value={campos['color'] ?? ''}
                onChange={(e) => set('color', e.target.value)}
              />
            </Campo>
          </>
        )}

        {pestana === 'tarjetones' && (
          <Campo
            etiqueta="Etiqueta"
            ayuda="Lo que se lee a simple vista en el plástico: «12», «A3». Es lo que el guardia pide de viva voz al recibirlo de vuelta."
          >
            <Entrada
              required
              autoFocus
              value={campos['etiqueta'] ?? ''}
              onChange={(e) => set('etiqueta', e.target.value)}
            />
          </Campo>
        )}

        {pestana === 'tags' && (
          <>
            {selectorUnidad}
            <Campo
              etiqueta="Código"
              ayuda="El EPC tal como lo publica el lector. Se normaliza al guardar."
            >
              <Entrada
                required
                value={campos['codigo'] ?? ''}
                onChange={(e) => set('codigo', e.target.value)}
              />
            </Campo>
            <Campo etiqueta="Tipo">
              <Seleccion
                value={campos['tipo'] ?? 'UHF_VEHICULAR'}
                onChange={(e) => set('tipo', e.target.value)}
              >
                {TIPOS_TAG.map((t) => (
                  <option key={t} value={t}>
                    {t.replace(/_/g, ' ').toLowerCase()}
                  </option>
                ))}
              </Seleccion>
            </Campo>

            {/* Opcional, y no por descuido: `Tag.vehiculoId` lo es en la base.
                Un engomado va pegado a un parabrisas, pero una tarjeta de
                proximidad la trae alguien en la bolsa y no pertenece a ningún
                coche. Exigirlo dejaría sin dar de alta las tarjetas. */}
            {vehiculos && (
              <Campo
                etiqueta="Vehículo"
                ayuda="Opcional. Un engomado va en un coche; una tarjeta no."
              >
                <Seleccion
                  value={campos['vehiculoId'] ?? ''}
                  onChange={(e) => set('vehiculoId', e.target.value)}
                >
                  <option value="">Sin vehículo</option>
                  {vehiculos.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.placa}
                    </option>
                  ))}
                </Seleccion>
                {alPedirVehiculo && (
                  <div className="mt-2">
                    {/* El desvío se lleva lo tecleado y lo devuelve con el
                        vehículo nuevo ya elegido. Mandar a alguien a otra
                        pantalla y que regrese a empezar de cero es cómo se
                        consigue que la credencial no se dé de alta. */}
                    <Boton variante="texto" onClick={() => alPedirVehiculo(campos)}>
                      {vehiculos.length
                        ? '¿No está el vehículo? Agrégalo'
                        : 'Esta vivienda no tiene vehículos. Agregar uno'}
                    </Boton>
                  </div>
                )}
              </Campo>
            )}
          </>
        )}

        {error && <ErrorCaja>{error}</ErrorCaja>}

        <div className="flex justify-end gap-2 pt-2">
          <Boton type="button" variante="texto" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton type="submit" variante="primario" disabled={enviando}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

/** Los campos vacíos se omiten: el API distingue "no enviado" de "vacío". */
/**
 * Quita los campos vacíos antes de mandar.
 *
 * `telefonosContacto` viaja aparte porque es una lista y aquí todo lo demás son
 * cadenas: se guarda separada por comas en el estado del formulario —que es un
 * mapa plano— y se parte al enviar.
 */
function limpiar(campos: Record<string, string>): Record<string, unknown> {
  const salida: Record<string, unknown> = Object.fromEntries(
    Object.entries(campos).filter(([clave, v]) => v !== '' && clave !== 'telefonosContacto'),
  );
  const contactos = (campos['telefonosContacto'] ?? '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  // Se manda si el campo se tocó, aunque quede vacío: borrar el último teléfono
  // tiene que poder guardarse. Omitirlo por estar vacío dejaba el número viejo
  // en la base sin que nadie lo viera en pantalla.
  if ('telefonosContacto' in campos) salida['telefonosContacto'] = contactos;
  return salida;
}

const TONO_TARJETON: Record<string, 'ok' | 'aviso' | 'malo' | 'neutro'> = {
  DISPONIBLE: 'ok',
  ENTREGADO: 'aviso',
  PERDIDO: 'malo',
  BAJA: 'neutro',
};

const TEXTO_TARJETON: Record<string, string> = {
  DISPONIBLE: 'En el cajón',
  ENTREGADO: 'Lo trae una visita',
  PERDIDO: 'Perdido',
  BAJA: 'De baja',
};

/**
 * Los tarjetones del condominio.
 *
 * Son los plásticos que el guardia le entrega a una visita sin invitación para
 * que tenga con qué cerrar su ciclo al salir. **El código lo genera Kodica**, y
 * de él sale el QR que se manda a imprimir: así el que va en el plástico y el
 * que está en la base son el mismo por construcción.
 *
 * La etiqueta importa más de lo que parece: es lo que el guardia dice en voz
 * alta —«devuélvame el 12»— y sin ella habría que deletrear un código.
 */
function TablaTarjetones({ filas, alCambiar }: { filas: Tarjeton[]; alCambiar: () => void }) {
  const [viendo, setViendo] = useState<Tarjeton | null>(null);

  async function cambiar(t: Tarjeton, estado: string) {
    let motivo: string | null = null;
    if (estado === 'PERDIDO') {
      // Se pide el motivo porque un tarjetón perdido alguien lo va a tener que
      // cobrar o reponer, y meses después nadie recuerda cuál fue cuál.
      motivo = window.prompt('¿Qué pasó con el tarjetón? Quién se lo llevó, o dónde se perdió');
      if (!motivo) return;
    }
    try {
      await pedir(`/tarjetones/${t.id}/estado`, { metodo: 'PATCH', cuerpo: { estado, motivo } });
      alCambiar();
    } catch (e) {
      window.alert(e instanceof ErrorApi ? e.message : 'No se pudo cambiar');
    }
  }

  return (
    <>
      <Tabla cabeceras={['Etiqueta', 'Estado', 'Quién lo trae', '']}>
        {filas.map((t) => {
          const quien = t.accesos[0];
          return (
            <Fila key={t.id}>
              <Celda fuerte>{t.etiqueta || '—'}</Celda>
              <Celda>
                <Chip tono={TONO_TARJETON[t.estado] ?? 'neutro'}>
                  {TEXTO_TARJETON[t.estado] ?? t.estado}
                </Chip>
                {t.motivoEstado && (
                  <p className="mt-1 text-label-sm text-on-surface-variant">{t.motivoEstado}</p>
                )}
              </Celda>
              <Celda>
                {quien ? (
                  <>
                    {quien.nombreVisitante ?? 'Sin nombre'}
                    <p className="text-label-sm text-on-surface-variant">
                      {[quien.unidad?.clave, quien.puntoAcceso?.nombre].filter(Boolean).join(' · ')}
                    </p>
                  </>
                ) : (
                  '—'
                )}
              </Celda>
              <Celda>
                <div className="flex justify-end gap-1">
                  <Boton variante="texto" onClick={() => setViendo(t)}>
                    Ver el QR
                  </Boton>
                  {/* Perder solo tiene sentido con el plástico fuera; devolverlo
                      al cajón, solo si no lo trae nadie. Ofrecer lo contrario
                      son botones que no pueden hacer nada. */}
                  {t.estado === 'ENTREGADO' && (
                    <Boton variante="texto" onClick={() => void cambiar(t, 'PERDIDO')}>
                      No lo devolvió
                    </Boton>
                  )}
                  {(t.estado === 'PERDIDO' || t.estado === 'BAJA') && (
                    <Boton variante="texto" onClick={() => void cambiar(t, 'DISPONIBLE')}>
                      Volvió al cajón
                    </Boton>
                  )}
                  {t.estado === 'DISPONIBLE' && (
                    <Boton variante="texto" onClick={() => void cambiar(t, 'BAJA')}>
                      Dar de baja
                    </Boton>
                  )}
                </div>
              </Celda>
            </Fila>
          );
        })}
      </Tabla>

      {viendo && <ModalQrTarjeton tarjeton={viendo} alCerrar={() => setViendo(null)} />}
    </>
  );
}

/**
 * El QR que se manda a imprimir.
 *
 * Se dibuja en el navegador y se imprime desde ahí: mandar una imagen por
 * correo o descargarla sería un paso más y un archivo suelto que se acaba
 * imprimiendo dos veces con códigos distintos.
 *
 * Debajo va la etiqueta en grande, porque es lo que se lee a un metro de
 * distancia — el QR lo lee la máquina, la etiqueta la lee el guardia.
 */
function ModalQrTarjeton({ tarjeton, alCerrar }: { tarjeton: Tarjeton; alCerrar: () => void }) {
  const [png, setPng] = useState<string | null>(null);

  useEffect(() => {
    // Margen 1 y no el de por omisión: el plástico ya tiene su propio borde, y
    // el margen grande de la librería encoge el código útil sin necesidad.
    void QRCode.toDataURL(tarjeton.codigo, { width: 420, margin: 1 }).then(setPng);
  }, [tarjeton.codigo]);

  return (
    <Modal titulo={`Tarjetón ${tarjeton.etiqueta || ''}`.trim()} abierto alCerrar={alCerrar}>
      <div className="flex flex-col items-center gap-4">
        {png ? (
          <img src={png} alt={`Código del tarjetón ${tarjeton.etiqueta ?? ''}`} className="w-64" />
        ) : (
          <p className="text-body-md text-on-surface-variant">Dibujando…</p>
        )}
        {tarjeton.etiqueta && (
          <p className="text-display-unit text-on-surface">{tarjeton.etiqueta}</p>
        )}
        <p className="text-center text-body-md text-on-surface-variant">
          Imprímelo y pégalo en el plástico. La etiqueta grande es la que el guardia va a pedir de
          viva voz.
        </p>
        <Boton variante="primario" onClick={() => window.print()}>
          Imprimir
        </Boton>
      </div>
    </Modal>
  );
}

/**
 * Si el residente puede entrar a la app, y el botón para dárselo o quitárselo.
 *
 * Hasta ahora no había ninguna forma de hacerlo: su usuario nacía sin
 * contraseña y nada en el producto podía ponerle una, así que la app del
 * residente estaba hecha y ningún residente real podía usarla.
 */
function AccesoApp({
  residente,
  alDar,
}: {
  residente: Residente;
  alDar: (d: { quien: string; identificador: string; clave: string }) => void;
}) {
  const [ocupado, setOcupado] = useState(false);
  const tiene = residente.usuario.tieneAcceso;
  const quien = `${residente.usuario.nombre} ${residente.usuario.apellidoPaterno ?? ''}`.trim();

  async function dar() {
    setOcupado(true);
    try {
      const r = await pedir<{ identificador: string; provisional: string }>(
        `/padron/residentes/${residente.id}/acceso`,
        { metodo: 'POST' },
      );
      alDar({ quien, identificador: r.identificador, clave: r.provisional });
    } catch (e) {
      window.alert(e instanceof ErrorApi ? e.message : 'No se pudo dar acceso');
    } finally {
      setOcupado(false);
    }
  }

  async function quitar() {
    if (
      !window.confirm(
        `¿Quitarle el acceso a la app a ${quien}? Sigue siendo residente: pierde la llave, no el lugar.`,
      )
    )
      return;
    setOcupado(true);
    try {
      await pedir(`/padron/residentes/${residente.id}/acceso`, { metodo: 'DELETE' });
      // Se recarga la página del padrón para que el estado de todos quede al
      // día: cambiar solo esta fila la dejaría discrepando del servidor.
      window.location.reload();
    } catch (e) {
      window.alert(e instanceof ErrorApi ? e.message : 'No se pudo quitar');
    } finally {
      setOcupado(false);
    }
  }

  if (tiene) {
    return (
      <div className="flex items-center gap-1">
        <Chip tono="ok">Entra</Chip>
        <Boton variante="texto" disabled={ocupado} onClick={() => void quitar()}>
          Quitar
        </Boton>
      </div>
    );
  }
  return (
    <Boton variante="texto" disabled={ocupado} onClick={() => void dar()}>
      {ocupado ? 'Generando…' : 'Dar acceso'}
    </Boton>
  );
}

/**
 * La contraseña provisional, enseñada **una sola vez**.
 *
 * No se guarda en claro y no se puede volver a consultar: si se pierde, se
 * genera otra. Es más seguro que poder recuperarla, y por eso el modal insiste
 * en que se anote antes de cerrarlo.
 */
function ModalAcceso({
  datos,
  alCerrar,
}: {
  datos: { quien: string; identificador: string; clave: string };
  alCerrar: () => void;
}) {
  const [copiado, setCopiado] = useState(false);

  return (
    <Modal titulo={`Acceso para ${datos.quien}`} abierto alCerrar={alCerrar}>
      <div className="space-y-4">
        <p className="text-body-md text-on-surface-variant">
          Pásale estos datos. La contraseña <b>no se puede volver a ver</b>: si se pierde, genera
          otra desde aquí.
        </p>

        <div className="rounded-xl bg-surface-container-high px-5 py-4 text-center">
          <p className="text-label-bold uppercase tracking-wider text-on-surface-variant">
            Entra con
          </p>
          <p className="mt-1 break-all font-mono text-body-lg text-on-surface">
            {datos.identificador}
          </p>
          <p className="mt-4 text-label-bold uppercase tracking-wider text-on-surface-variant">
            Contraseña provisional
          </p>
          {/* Grande y en monoespaciada: esto se dicta por teléfono mientras la
              otra persona anota, y se lee en grupos de tres. */}
          <p className="mt-1 font-mono text-display-unit tracking-wider text-primary">
            {datos.clave}
          </p>
        </div>

        <p className="text-body-md text-on-surface-variant">
          Al entrar, la app le va a pedir que escoja la suya. Esta solo sirve para la primera vez.
        </p>

        <div className="flex justify-end gap-2">
          <Boton
            variante="tonal"
            onClick={() => {
              void copiarTexto(`${datos.identificador}
${datos.clave}`).then(setCopiado);
            }}
          >
            {copiado ? 'Copiado' : 'Copiar'}
          </Boton>
          <Boton variante="primario" onClick={alCerrar}>
            Ya la anoté
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Cuántos residentes no tienen teléfono, y quiénes.
 *
 * El teléfono ya es obligatorio al dar de alta a alguien, pero eso no arregla a
 * quien ya estaba. Esto existe para que esos huecos **se puedan cerrar**: sin
 * una lista, «hay que capturar los teléfonos que faltan» es una intención; con
 * ella son doce nombres y una tarde.
 *
 * Y no es cosmético: sin teléfono, ese residente no podrá recuperar su
 * contraseña él solo, y cada vez que la olvide habrá que atenderlo a mano.
 *
 * Se calla cuando no hay ninguno. Un aviso que dice «0 pendientes» ocupa sitio
 * para no decir nada, y enseña a no leer los avisos de esa pantalla.
 */
function SinTelefono({ admin }: { admin: boolean }) {
  const faltantes = useRecurso<{
    cuantos: number;
    residentes: Array<{ id: string; nombre: string; vivienda: string; tipo: string }>;
  }>(admin ? '/padron/residentes/sin-telefono' : null);

  if (!faltantes.datos || faltantes.datos.cuantos === 0) return null;
  const { cuantos, residentes } = faltantes.datos;

  return (
    <details className="mb-4 rounded-xl border border-outline-variant bg-surface-container-low px-6 py-4">
      <summary className="cursor-pointer text-body-md text-on-surface">
        <strong>
          {cuantos} residente{cuantos === 1 ? '' : 's'} sin teléfono
        </strong>{' '}
        — no podrá{cuantos === 1 ? '' : 'n'} recuperar su contraseña sin ayuda de la administración.
      </summary>

      <ul className="mt-3 flex flex-col gap-1">
        {residentes.map((r) => (
          <li key={r.id} className="text-body-md text-on-surface-variant">
            {r.nombre} — {r.vivienda || 'sin vivienda'} ({r.tipo.toLowerCase()})
          </li>
        ))}
      </ul>
    </details>
  );
}
